/**
 * The application frame: icon rail, top bar, content region.
 *
 * Responsive behaviour — the accepted three-tier rule (mockups 2a "THE SYSTEM", 7a phone
 * browser, 7b tablet browser; restated in `config/hrms-tokens.css`'s breakpoint block):
 *
 *   >= 1200 (desktop)  a persistent 68px ICON-ONLY rail. No labels — a section with more than
 *                       one reachable child reveals them in a flyout on hover/focus.
 *   768-1199 (tablet)  the same rail, narrower at 64px. Content drops to one column
 *                       (handled per-page via `grid-cols-content desktop:...`, not here).
 *   < 768 (phone)      no rail at all. Navigation becomes a horizontally scrolling CHIP ROW
 *                       under the header — the browser already owns the bottom edge on a
 *                       phone, so a second fixed bar down there is the wrong instinct.
 *
 * The `.app-shell` grid itself (which column the rail occupies, and at which widths) is
 * design-agent's in `globals.css`; this component only renders into it.
 *
 * There is no "collapsed / expanded" sidebar in the accepted design — the rail is icon-only
 * at every tier it exists. The previous 248px label-bearing sidebar and its collapse toggle
 * are retired, not made optional.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LogOut, Menu, Moon, PanelLeftClose, PanelLeftOpen, Search, Sun, X } from 'lucide-react';

import { useAuth } from '@frontend/auth/AuthProvider';
import { useNavigation } from '@frontend/api/hooks/platform';
import { usePendingApprovals } from '@frontend/api/hooks/workflows';
import { cn } from '@frontend/lib/cn';
import { initialsOf } from '@frontend/lib/format';
import { usePreferences } from '@frontend/store/preferences';
import { IconButton } from '@frontend/components/ui/Button';
import { NotificationBell } from '@frontend/components/notifications/NotificationBell';
import { visibleSections, type NavItem, type NavSection } from './navigation';

export function AppShell(): JSX.Element {
  const { user, hasAny, signOut } = useAuth();
  const navigationQuery = useNavigation();
  const pending = usePendingApprovals();
  const {
    mobileNavOpen,
    setMobileNavOpen,
    resolvedTheme,
    toggleTheme,
    sidebarCollapsed,
    toggleSidebar,
  } = usePreferences();
  // The stored preference is "collapsed", and collapsed is the accepted resting state — so the
  // rail is expanded only when somebody has explicitly asked for it, and that choice persists.
  const railExpanded = !sidebarCollapsed;

  const enabledModules = useMemo(() => {
    if (navigationQuery.data === undefined) return null;
    return new Set(navigationQuery.data.items.map((item) => item.moduleKey));
  }, [navigationQuery.data]);

  const sections = useMemo(
    () => visibleSections(hasAny, enabledModules),
    [hasAny, enabledModules],
  );
  // The rail (and the phone chip row) are icon/pill-only — there is no room for the section
  // labels the full sidebar used to render, so navigation is flattened to one ordered list of
  // top-level items. A user reaches a section's children either from the flyout (rail) or
  // from in-page tabs once they land on the section.
  const items = useMemo(() => sections.flatMap((section) => section.items), [sections]);

  const pendingCount = pending.data?.items.length ?? 0;

  return (
    <div className="app-shell" data-rail={railExpanded ? 'expanded' : 'rail'}>
      {/* ── Icon rail (tablet and up) ─────────────────────────────────── */}
      <aside
        className={cn(
          'sticky top-0 hidden h-dvh flex-col bg-surface py-[18px] tablet:flex',
          railExpanded ? 'items-start px-2' : 'items-center',
        )}
        data-testid="sidebar"
        data-expanded={railExpanded}
      >
        <RailBrand expanded={railExpanded} />
        <IconRail items={items} pendingCount={pendingCount} expanded={railExpanded} />
        <RailToggle expanded={railExpanded} onToggle={toggleSidebar} />
        <RailAvatar name={user?.displayName} />
      </aside>

      {/* ── Phone drawer — a full menu, reachable from the hamburger, for anything not on
          the chip row (settings, sign out, less-frequent sections). ─────────────────────── */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-drawer tablet:hidden">
          <div
            className="absolute inset-0 animate-fade-in bg-surface-overlay"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden
          />
          <aside className="absolute left-0 top-0 flex h-full w-[280px] max-w-[85vw] animate-drawer-in flex-col bg-surface shadow-float">
            <div className="flex items-center justify-between px-3 py-3">
              <BrandMark />
              <IconButton
                variant="ghost"
                size="sm"
                label="Close navigation"
                icon={<X className="h-4 w-4" />}
                onClick={() => setMobileNavOpen(false)}
              />
            </div>
            <DrawerNav sections={sections} pendingCount={pendingCount} onNavigate={() => setMobileNavOpen(false)} />
          </aside>
        </div>
      )}

      {/* ── Main column ──────────────────────────────────────────────── */}
      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-topbar flex h-topbar flex-none items-center gap-2 bg-surface px-3 shadow-card tablet:px-gutter-tablet desktop:px-gutter-desktop">
          <IconButton
            variant="ghost"
            size="sm"
            label="Open navigation"
            icon={<Menu className="h-4 w-4" />}
            className="tablet:hidden"
            onClick={() => setMobileNavOpen(true)}
            data-testid="open-nav"
          />
          <div className="tablet:hidden">
            <BrandMark />
          </div>
          <div className="hidden items-center gap-2 rounded-md bg-surface-subtle px-3 py-2 tablet:flex">
            <Search aria-hidden className="h-3.5 w-3.5 text-content-tertiary" />
            <span className="text-body text-content-tertiary">Search</span>
          </div>

          <div className="ml-auto flex items-center gap-1">
            <IconButton
              variant="ghost"
              size="sm"
              label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              icon={resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              onClick={toggleTheme}
              data-testid="toggle-theme"
            />
            <NotificationBell />
            <div className="ml-1 flex items-center gap-2 pl-2">
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full bg-interactive-subtle text-eyebrow font-mono text-content"
                aria-hidden
              >
                {initialsOf(user?.displayName)}
              </div>
              <div className="hidden min-w-0 tablet:block">
                <p className="truncate text-row leading-tight text-content">
                  {user?.displayName ?? 'Signed in'}
                </p>
                <p className="truncate text-data font-mono leading-tight text-content-secondary">
                  {user?.email ?? ''}
                </p>
              </div>
              <IconButton
                variant="ghost"
                size="sm"
                label="Sign out"
                icon={<LogOut className="h-4 w-4" />}
                onClick={() => {
                  void signOut();
                }}
                data-testid="sign-out"
              />
            </div>
          </div>
        </header>

        {/* ── Phone chip row — replaces the rail below 768px (mockup 7a). A scrolling row
            of pills, not a fixed tab bar: the browser chrome already owns the bottom edge
            on a phone, so a second bar down there fights it rather than earning its space. */}
        <ChipRow items={items} pendingCount={pendingCount} />

        <main id="main-content" className="min-w-0 flex-1" tabIndex={-1}>
          <div className="page-container">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function BrandMark(): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      {/*
        The real mark, not a letter in a box. The colour logo carries its own coral, so it sits
        on the surface unaltered — this is the one place brand colour belongs, and it arrives in
        the file rather than from a token.

        The LOGOMARK, not the full logo: asb-logo.svg is a wordmark that already reads
        "asb infotech", so pairing it with a text label printed the company name twice. One mark
        plus one name — and the name is the product, "asb people", which is what the login page
        calls it and what the user is actually inside. The mark is coral on both surfaces, so it
        needs no light/dark pair.

        No `font-semibold`: Hammersmith One has a single weight, and asking for 600 makes the
        browser fake a bold that reads as a rendering fault.
      */}
      <img
        src="/brand/logo/asb-logomark.svg"
        alt=""
        aria-hidden
        className="h-7 w-7 shrink-0"
      />
      <span className="font-display text-row text-content">asb people</span>
    </div>
  );
}

function RailBrand({ expanded }: { expanded: boolean }): JSX.Element {
  // Three bugs lived here. It was 26px inside a 68px rail — a stamp, not a mark. It carried
  // `dark:hidden` with NO dark sibling, so in dark mode the logo did not shrink, it vanished.
  // And it was boxed `h-9 w-9` around the 3154x782 WORDMARK, so `object-contain` fitted it to
  // its width and left it 9px tall — the whole company name rendered as a smear.
  //
  // A 4:1 wordmark cannot be read in a 68px rail at any size, and beside a text label it prints
  // the company name twice — the file already reads "asb infotech". So both states use
  // asb-logomark.svg, the coral mark alone, cropped out of the wordmark file: square, so it
  // fills the rail the way every icon below it does, and coral on both surfaces, so unlike the
  // wordmark it needs no light/dark pair. Expanded adds the product name beside it, once.
  return (
    <div className={cn('mb-4 flex shrink-0 items-center gap-2.5', expanded ? 'w-full px-2' : '')}>
      <img src="/brand/logo/asb-logomark.svg" alt="asb people" className="h-10 w-10 shrink-0" />
      {expanded && <span className="truncate text-section text-content">asb people</span>}
    </div>
  );
}

/**
 * Expand / collapse the rail.
 *
 * The accepted mockups show an icon-only rail with no expanded state, so this is an addition
 * to the design rather than a reading of it. It earns its place on one fact: mockup 2a's rail
 * carries about seven icons, and this product's navigation is roughly fifteen. Fifteen
 * unlabelled glyphs is a memory test, and the people who pay for it are the ones who use the
 * product least often.
 *
 * Collapsed remains the RESTING state, so the design's appearance out of the box is unchanged.
 * The expansion is opt-in and remembered, which means it costs a first-time user nothing and
 * costs an occasional user one click, once.
 */
function RailToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid="toggle-rail"
      aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
      title={expanded ? 'Collapse navigation' : 'Expand navigation'}
      className={cn(
        'mt-1 flex h-11 items-center rounded-md text-content-tertiary transition-token hover:bg-surface-hover hover:text-content',
        expanded ? 'w-full gap-2.5 px-2.5' : 'w-11 justify-center',
      )}
    >
      {expanded ? (
        <PanelLeftClose aria-hidden className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
      ) : (
        <PanelLeftOpen aria-hidden className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
      )}
      {expanded && <span className="truncate text-row">Collapse</span>}
    </button>
  );
}

function RailAvatar({ name }: { name: string | undefined }): JSX.Element {
  return (
    <div
      aria-hidden
      className="mt-auto flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-eyebrow font-mono text-content-secondary"
    >
      {initialsOf(name)}
    </div>
  );
}

/**
 * Icon-only navigation for the rail. A section with reachable children (`landingFor` already
 * filtered them to what the user may open) reveals them in a flyout on hover/focus rather than
 * inline — there is no room for a nested list at 64-68px wide.
 */
function IconRail({
  items,
  pendingCount,
  expanded,
}: {
  items: NavItem[];
  pendingCount: number;
  expanded: boolean;
}): JSX.Element {
  return (
    <nav
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-[5px] overflow-y-auto no-scrollbar',
        expanded ? 'w-full px-2' : 'items-center',
      )}
      aria-label="Main"
    >
      {items.map((item) => (
        <RailItem key={item.key} item={item} pendingCount={pendingCount} expanded={expanded} />
      ))}
    </nav>
  );
}

function RailItem({
  item,
  pendingCount,
  expanded,
}: {
  item: NavItem;
  pendingCount: number;
  expanded: boolean;
}): JSX.Element {
  const location = useLocation();
  const Icon = item.icon;
  const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
  // Expanded shows a label, so the flyout is only the COLLAPSED affordance.
  const flyoutChildren = !expanded && (item.children?.length ?? 0) > 1 ? item.children : undefined;
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const closeTimer = useRef<number>();

  function openFlyout(): void {
    window.clearTimeout(closeTimer.current);
    setFlyoutOpen(true);
  }
  function closeFlyoutSoon(): void {
    closeTimer.current = window.setTimeout(() => setFlyoutOpen(false), 120);
  }

  return (
    <div
      className="group relative"
      onMouseEnter={openFlyout}
      onMouseLeave={closeFlyoutSoon}
      onFocus={openFlyout}
      onBlur={closeFlyoutSoon}
    >
      <NavLink
        to={item.to}
        end={item.to === '/'}
        data-testid={`nav-${item.key}`}
        aria-label={item.label}
        title={item.label}
        className={cn(
          'relative flex h-11 items-center rounded-md transition-token',
          expanded ? 'w-full gap-2.5 px-2.5' : 'w-11 justify-center',
          isActive
            ? 'bg-content text-content-inverse'
            : 'text-content-tertiary hover:bg-surface-hover hover:text-content',
        )}
      >
        <Icon aria-hidden className="h-[18px] w-[18px] shrink-0" strokeWidth={1.6} />
        {expanded && <span className="truncate text-row">{item.label}</span>}
        {/* The rail's one coral moment: a dot on "My approvals" when something is waiting —
            never a filled badge count here, there is no room for one at this width. */}
        {item.badge === 'approvals' && pendingCount > 0 && (
          <span
            aria-hidden
            className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-brand"
            data-testid="nav-approvals-dot"
          />
        )}
      </NavLink>

      {flyoutChildren !== undefined && flyoutOpen && (
        <div
          role="menu"
          className="absolute left-full top-0 z-dropdown ml-2 min-w-[190px] rounded-md bg-surface p-1.5 shadow-float"
        >
          <p className="px-2 py-1 text-eyebrow font-mono uppercase text-content-tertiary">{item.label}</p>
          {flyoutChildren.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              end
              role="menuitem"
              className={({ isActive: childActive }) =>
                cn(
                  'block rounded-sm px-2 py-1.5 text-row transition-token',
                  childActive ? 'bg-surface-selected text-content' : 'text-content-secondary hover:text-content',
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/** Scrolling chip row — the phone replacement for the rail (mockup 7a). */
function ChipRow({ items, pendingCount }: { items: NavItem[]; pendingCount: number }): JSX.Element {
  const location = useLocation();
  return (
    <nav
      aria-label="Main"
      className="flex flex-none gap-[7px] overflow-x-auto px-gutter-phone py-3 no-scrollbar tablet:hidden"
    >
      {items.map((item) => {
        const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
        return (
          <NavLink
            key={item.key}
            to={item.to}
            end={item.to === '/'}
            data-testid={`nav-chip-${item.key}`}
            className={cn(
              'flex h-9 flex-none items-center gap-1.5 whitespace-nowrap rounded-2xl px-3.5 text-row transition-token',
              isActive
                ? 'bg-content text-content-inverse'
                : 'bg-surface-subtle text-content-secondary',
            )}
          >
            {item.label}
            {item.badge === 'approvals' && pendingCount > 0 && (
              <span
                aria-hidden
                className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 font-mono text-eyebrow text-content-inverse"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

/** Full section list for the phone drawer — everything the chip row has no room for. */
function DrawerNav({
  sections,
  pendingCount,
  onNavigate,
}: {
  sections: NavSection[];
  pendingCount: number;
  onNavigate: () => void;
}): JSX.Element {
  return (
    <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-2" aria-label="Main">
      {sections.map((section) => (
        <div key={section.key} className="mb-3">
          {section.label !== null && (
            <p className="px-2 py-1 text-eyebrow font-mono uppercase text-content-tertiary">{section.label}</p>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.key}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-touch items-center gap-2 rounded-md px-2 py-1.5 text-row transition-token',
                        isActive
                          ? 'bg-surface-selected font-medium text-content'
                          : 'text-content-secondary hover:bg-surface-hover hover:text-content',
                      )
                    }
                  >
                    <Icon aria-hidden className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {item.badge === 'approvals' && pendingCount > 0 && (
                      <span
                        aria-hidden
                        className="ml-auto flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 font-mono text-eyebrow text-content-inverse"
                      >
                        {pendingCount > 99 ? '99+' : pendingCount}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

export function ShellFallback({ children }: { children: ReactNode }): JSX.Element {
  return <div className="page-container">{children}</div>;
}
